// src/pages/IndoorMap/RouteInfoPanel.jsx
import React, { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { LOCATIONS } from "./locationsData"
import { getInstructions } from "./routeInstructions"
import { getStepIcon } from "./stepIcons"
import LocationCard from "./LocationCard"

const RouteInfoPanel = ({ startId, endId }) => {
  const [open, setOpen] = useState(false)
  const [renderInstructions, setRenderInstructions] = useState(false)

  const dividerRef = useRef(null)
  const bubbleRef = useRef(null)
  const startCardRef = useRef(null)
  const endCardRef = useRef(null)
  const instructionsRef = useRef(null)
  const prevEndId = useRef(endId)

  const startLocation = LOCATIONS[startId]
  const endLocation = endId ? LOCATIONS[endId] : null
  const instructions = startId && endId ? getInstructions(startId, endId) : null

  // Entrance: divider grows -> bubble container fades/scales in -> start card slides in
  useEffect(() => {
    if (!startLocation) return
    const tl = gsap.timeline()
    tl.fromTo(
      dividerRef.current,
      { scaleX: 0, opacity: 0 },
      { scaleX: 1, opacity: 1, duration: 0.5, ease: "power2.out" }
    )
      .fromTo(
        bubbleRef.current,
        { opacity: 0, y: 16, scale: 0.97 },
        { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: "back.out(1.6)" },
        "-=0.1"
      )
      .fromTo(
        startCardRef.current,
        { opacity: 0, x: -14 },
        { opacity: 1, x: 0, duration: 0.4, ease: "power2.out" },
        "-=0.2"
      )
    return () => tl.kill()
  }, [startId])

  // Destination card slides in the moment endId newly appears; closes panel if cleared
  useEffect(() => {
    if (endId && !prevEndId.current && endCardRef.current) {
      gsap.fromTo(
        endCardRef.current,
        { opacity: 0, y: -10 },
        { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }
      )
    }
    if (!endId) setOpen(false)
    prevEndId.current = endId
  }, [endId])

  // Toggle: mount instructions when opening; animate out then unmount when closing
  useEffect(() => {
    if (open) {
      setRenderInstructions(true)
      return
    }
    if (renderInstructions && instructionsRef.current) {
      const steps = instructionsRef.current.querySelectorAll(".route-step")
      gsap
        .timeline({ onComplete: () => setRenderInstructions(false) })
        .to(steps, { opacity: 0, x: -10, duration: 0.2, stagger: 0.03 })
        .to(instructionsRef.current, { height: 0, marginTop: 0, marginBottom: 0, duration: 0.25 }, "-=0.05")
    }
  }, [open])

  // Once mounted, animate the space open + stagger steps in
  useEffect(() => {
    if (!renderInstructions || !instructionsRef.current) return
    const el = instructionsRef.current
    const steps = el.querySelectorAll(".route-step")
    gsap.set(el, { height: "auto", marginTop: 14, marginBottom: 14 })
    const naturalHeight = el.offsetHeight
    gsap.fromTo(
      el,
      { height: 0, marginTop: 0, marginBottom: 0 },
      { height: naturalHeight, marginTop: 14, marginBottom: 2, duration: 0.3, ease: "power2.out" }
    )
    gsap.fromTo(
      steps,
      { opacity: 0, x: -14 },
      { opacity: 1, x: 0, duration: 0.35, stagger: 0.08, ease: "power2.out", delay: 0.1 }
    )
  }, [renderInstructions])

  if (!startLocation) return null

  const canToggle = !!(startLocation && endLocation)

  return (
    <div className="route-info-wrap">
      <div ref={dividerRef} className="route-info-divider" />

      <div
        ref={bubbleRef}
        className={`route-bubble-container ${canToggle ? "is-clickable" : ""} ${open ? "is-open" : ""}`}
        onClick={() => canToggle && setOpen(o => !o)}
        role={canToggle ? "button" : undefined}
        tabIndex={canToggle ? 0 : undefined}
      >
        <div ref={startCardRef}>
          <LocationCard location={startLocation} tag="You are here" />
        </div>

        {renderInstructions && instructions && (
          <div ref={instructionsRef} className="route-instructions">
            {instructions.map((step, i) => {
              const StepIcon = getStepIcon(step)
              return (
                <div className="route-step" key={i}>
                  <span className="route-step-icon">
                    <StepIcon size={16} />
                  </span>
                  <span className="route-step-text">{step}</span>
                </div>
              )
            })}
          </div>
        )}

        {endLocation && (
          <div ref={endCardRef}>
            <LocationCard location={endLocation} tag="Destination" />
          </div>
        )}

        {canToggle && (
          <div className="route-bubble-hint">
            {open ? "Tap to hide directions" : "Tap to view directions"}
          </div>
        )}
      </div>
    </div>
  )
}

export default RouteInfoPanel